import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Search, Filter, Users, TrendingUp, Mail, Eye, ArrowLeft, Sparkles } from "lucide-react";
import LeadDetailModal from "@/components/LeadDetailModal";

const AccountsView = () => {
  const { userId } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("accounts"); // accounts or leads
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts/${userId}`);
      setAccounts(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to load accounts");
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads/${userId}`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchLeads();
  }, [userId]); // eslint-disable-line

  useEffect(() => {
    const data = viewMode === "accounts" ? accounts : leads;
    let filtered = data;

    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.contactName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    setFilteredAccounts(filtered);
  }, [accounts, leads, searchQuery, statusFilter, viewMode]);

  const getStatusColor = (status) => {
    switch (status) {
      case "customer": return "bg-emerald-500/10 text-emerald-400/80 border-emerald-500/30";
      case "qualified": return "bg-blue-500/10 text-blue-400/80 border-blue-500/30";
      case "engaged": return "bg-amber-500/10 text-amber-400/80 border-amber-500/30";
      case "prospect": return "bg-gray-500/10 text-gray-400/80 border-gray-500/30";
      case "new": return "bg-cyan-500/10 text-cyan-400/80 border-cyan-500/30";
      default: return "bg-gray-500/10 text-gray-400/80 border-gray-500/30";
    }
  };

  const getEngagementColor = (score) => {
    if (score >= 70) return "text-emerald-400/80";
    if (score >= 40) return "text-amber-400/80";
    return "text-red-400/80";
  };

  const handleBulkAction = async (action) => {
    toast.info(`${action} action initiated for selected accounts`);
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleLeadUpdated = (updatedLead) => {
    setLeads(prev => prev.map(l => l.leadId === updatedLead.leadId ? updatedLead : l));
    setSelectedLead(updatedLead);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-cyan-400/50"></div>
      </div>
    );
  }

  if (selectedAccount) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6" 
        data-testid="account-detail"
      >
        <div className="flex items-center justify-between">
          <Button
            onClick={() => setSelectedAccount(null)}
            variant="ghost"
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Accounts
          </Button>
        </div>

        <Card className="glass-card rounded-xl p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl text-white mb-2">{selectedAccount.companyName}</h2>
              <p className="text-gray-500">{selectedAccount.industry}</p>
            </div>
            <Badge className={`${getStatusColor(selectedAccount.status)} border text-sm`}>
              {selectedAccount.status}
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="glass-card-light rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Target Persona</p>
              <p className="text-base text-white">{selectedAccount.targetPersona}</p>
            </div>
            <div className="glass-card-light rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Engagement Score</p>
              <p className={`text-base ${getEngagementColor(selectedAccount.engagementScore)}`}>
                {selectedAccount.engagementScore}/100
              </p>
            </div>
            <div className="glass-card-light rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Last Contact</p>
              <p className="text-base text-white">
                {selectedAccount.lastContact ? new Date(selectedAccount.lastContact).toLocaleDateString() : "Never"}
              </p>
            </div>
            <div className="glass-card-light rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Next Action</p>
              <p className="text-base text-cyan-400/80">{selectedAccount.nextAction || "TBD"}</p>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6">
            <h3 className="text-sm text-gray-400 mb-4 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-cyan-400/50" />
              AI-Generated Insights
            </h3>
            <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                Based on {selectedAccount.companyName}&apos;s profile and engagement history, we recommend focusing on {selectedAccount.targetPersona}. 
                The account shows {selectedAccount.engagementScore >= 50 ? "strong" : "moderate"} engagement signals. 
                Key talking points should emphasize ROI and efficiency gains in their {selectedAccount.industry} operations.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button className="bg-gradient-to-r from-pink-600/80 to-pink-500/80 hover:from-pink-500/80 hover:to-pink-400/80 text-white">
              <Mail className="h-4 w-4 mr-2" />
              Generate Outreach
            </Button>
            <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5">
              Update Status
            </Button>
          </div>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6" 
      data-testid="accounts-view"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-white mb-2 flex items-center gap-2">
            <Users className="h-6 w-6 text-neon-cyan" />
            <span className="text-neon-cyan">Accounts & Leads</span>
          </h1>
          <p className="text-sm text-gray-500">Manage and track all customer accounts and leads</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => handleBulkAction('Campaign')}
            variant="outline"
            className="border-white/20 text-gray-300 hover:bg-white/5"
          >
            Assign to Campaign
          </Button>
          <Button
            onClick={() => handleBulkAction('Outreach')}
            className="bg-gradient-to-r from-pink-600/80 to-pink-500/80 hover:from-pink-500/80 hover:to-pink-400/80 text-white"
          >
            <Mail className="h-4 w-4 mr-2" />
            Generate Outreach
          </Button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex gap-2 glass-card-light p-1 rounded-lg w-fit">
        <Button
          variant={viewMode === "accounts" ? "default" : "ghost"}
          size="sm"
          onClick={() => setViewMode("accounts")}
          className={viewMode === "accounts" 
            ? "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30" 
            : "text-gray-400 hover:text-white hover:bg-white/10"}
          data-testid="view-accounts-btn"
        >
          Accounts ({accounts.length})
        </Button>
        <Button
          variant={viewMode === "leads" ? "default" : "ghost"}
          size="sm"
          onClick={() => setViewMode("leads")}
          className={viewMode === "leads" 
            ? "bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 border border-pink-500/30" 
            : "text-gray-400 hover:text-white hover:bg-white/10"}
          data-testid="view-leads-btn"
        >
          Leads ({leads.length})
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search accounts..."
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="search-accounts"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white/5 border border-white/10 text-white rounded-lg px-4 py-2 text-sm"
          data-testid="status-filter"
        >
          <option value="all">All Status</option>
          <option value="prospect">Prospect</option>
          <option value="engaged">Engaged</option>
          <option value="qualified">Qualified</option>
          <option value="customer">Customer</option>
        </select>
      </div>

      {/* Accounts/Leads Table */}
      <Card className="glass-card rounded-xl overflow-hidden">
        <ScrollArea className="h-[600px]">
          {viewMode === "accounts" ? (
            <>
              <table className="w-full">
                <thead className="border-b border-white/10 sticky top-0 bg-black/60 backdrop-blur-sm">
                  <tr>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Industry</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Persona</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Engagement</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Last Contact</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Next Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account, idx) => (
                    <motion.tr
                      key={account.accountId}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => handleAccountClick(account)}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`account-row-${idx}`}
                    >
                      <td className="p-4">
                        <p className="text-white">{account.companyName}</p>
                      </td>
                      <td className="p-4 text-gray-400 text-sm">{account.industry}</td>
                      <td className="p-4 text-gray-400 text-sm">{account.targetPersona}</td>
                      <td className="p-4">
                        <Badge className={`${getStatusColor(account.status)} border text-xs`}>
                          {account.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <span className={`${getEngagementColor(account.engagementScore)}`}>
                          {account.engagementScore}
                        </span>
                      </td>
                      <td className="p-4 text-gray-400 text-sm">
                        {account.lastContact ? new Date(account.lastContact).toLocaleDateString() : "Never"}
                      </td>
                      <td className="p-4 text-cyan-400/70 text-sm">{account.nextAction || "TBD"}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {filteredAccounts.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No accounts found</p>
                </div>
              )}
            </>
          ) : (
            <>
              <table className="w-full">
                <thead className="border-b border-white/10 sticky top-0 bg-black/60 backdrop-blur-sm">
                  <tr>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Industry</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Score</th>
                    <th className="text-left p-4 text-xs text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((lead, idx) => (
                    <motion.tr
                      key={lead.leadId}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`lead-row-${idx}`}
                    >
                      <td className="p-4">
                        <p className="text-white">{lead.companyName}</p>
                      </td>
                      <td className="p-4 text-gray-300 text-sm">{lead.contactName || "-"}</td>
                      <td className="p-4 text-gray-400 text-sm">{lead.contactTitle || "-"}</td>
                      <td className="p-4 text-gray-400 text-sm">{lead.industry}</td>
                      <td className="p-4">
                        <Badge className={`${getStatusColor(lead.status)} border text-xs`}>
                          {lead.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <span className={`${getEngagementColor(lead.score || 0)}`}>
                          {lead.score || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleLeadClick(lead)}
                          className="text-cyan-400/80 hover:text-cyan-300 hover:bg-cyan-500/10"
                          data-testid={`view-lead-btn-${idx}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {filteredAccounts.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No leads found</p>
                  <p className="text-gray-500 text-sm mt-1">Use Hunt mode to generate leads</p>
                </div>
              )}
            </>
          )}
        </ScrollArea>
      </Card>

      {/* Lead Detail Modal */}
      <LeadDetailModal
        isOpen={showLeadModal}
        onClose={() => { setShowLeadModal(false); setSelectedLead(null); }}
        lead={selectedLead}
        userId={userId}
        onLeadUpdated={handleLeadUpdated}
      />
    </motion.div>
  );
};

export default AccountsView;
